import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    IconButton,
    Input,
    InputAdornment,
    makeStyles,
    Theme,
} from '@material-ui/core'
import ClearIcon from '@material-ui/icons/Clear'
import SearchIcon from '@material-ui/icons/Search'
import clsx from 'clsx'
import React from 'react'
import { StudioContext, StudioContextValue } from '../common/StudioContext'

interface StyleProps {
    searchbarWidth: number
}

const useStyles = makeStyles((theme: Theme) => ({
    searchFieldText: {
        fontSize: pxToRem(13),
        marginLeft: theme.spacing(0.5),
        marginTop: theme.spacing(0.2),
        padding: theme.spacing(0.875, 0, 0.875),
    },
    searchContainer: {
        position: 'relative',
        display: 'inline-block',
        height: '30px',
        width: '30px',
        background: theme.palette.common.white,
        borderRadius: '100%',
        textAlign: 'center',
    },
    mglass: {
        display: 'inline-block',
        pointerEvents: 'none',
        WebkitTransform: 'rotate(-45deg)',
        MozTransform: 'rotate(-45deg)',
        OTransform: 'rotate(-45deg)',
        MsTransform: 'rotate(-45deg)',
        color: '#2f3843',
    },
    searchButton: {
        position: 'absolute',
        fontSize: '22px',
        width: '100%',
        margin: 0,
        padding: 0,
        right: 0,
    },
    search: {
        position: 'absolute',
        left: 'auto',
        backgroundColor: 'white',
        outline: 'none',
        border: 'none',
        padding: 0,
        right: 0,
        height: '100%',
        margin: 0,
        transitionDuration: '0.4s',
        MozTransitionDuration: '0.4s',
        WebkitTransitionDuration: '0.4s',
        OTransitionDuration: '0.4s',
        zIndex: -1,
        opacity: 0,
    },
    expandRight: {
        borderRadius: '10px',
        padding: '0 0 0 16px',
        zIndex: 1,
        opacity: 1,
        width: (props: StyleProps) => props.searchbarWidth,
    },
    searchFieldItem: {
        height: '100%',
        width: '100%',
        paddingLeft: theme.spacing(1),
        '& .MuiInputAdornment-positionEnd': {
            position: 'absolute',
            right: 0,
            margin: 0,
        },
    },
    adornmentButton: {
        padding: theme.spacing(0),
        zIndex: 1,
        '&:hover': {
            background: 'transparent',
        },
    },
    clearIcon: {
        color: 'rgba(0, 0, 0, 0.54)',
        fontSize: pxToRem(24),
    },
    searchIcon: {
        background: theme.palette.common.white,
        borderRadius: '100%',
        width: pxToRem(30),
        textAlign: 'center',
        cursor: 'pointer',
        color: '#233642',
        zindex: 10,
        '& svg': {
            marginTop: pxToRem(4),
            fontSize: pxToRem(20),
        },
    },
    searchIconDisabled: {
        color: '#23364238',
    },
}))

const searchRegex = /\s/g

export interface ElementPanelSearchProps {
    panelTitle: string
    setBPSearchValue:
        | ((value?: string | boolean | null, panel?: string | null) => void)
        | undefined
    width: number
}

const ElementPanelSearch: React.FC<ElementPanelSearchProps> = ({
    panelTitle,
    setBPSearchValue,
    width,
}) => {
    const classes = useStyles({
        searchbarWidth: width - 24,
    })

    const {
        searchValue,
        setSearchValue,
        expandSearch,
        setExpandSearch,
        isSearched,
        setIsSearched,
    } = React.useContext(StudioContext) as StudioContextValue

    const titleLowerCase = React.useMemo(
        () => panelTitle.toLowerCase() as keyof typeof searchValue,
        [panelTitle]
    )

    const handleSearchValue = React.useCallback(
        (event) => {
            setSearchValue({
                ...searchValue,
                [titleLowerCase]: event.target.value,
            })
        },
        [searchValue]
    )

    const isValueAllowedForSearch = React.useCallback(
        (type?: string) => {
            const searchText = searchValue[titleLowerCase] as string
            const searchTextLength = searchText?.replace(searchRegex, '')
                ?.length
            return type ? searchTextLength <= 2 : searchTextLength >= 3
        },
        [searchValue]
    )

    const handleSearchKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter' && isValueAllowedForSearch()) {
                setBPSearchValue?.(searchValue[titleLowerCase], panelTitle)
                setIsSearched({
                    ...isSearched,
                    [titleLowerCase]: true,
                })
            }
        },
        [searchValue, isSearched]
    )

    const handleSearchExpand = React.useCallback(
        (value: boolean) => {
            if (value) {
                document.getElementById(`searchinput${panelTitle}`)!.focus()
            }
            setExpandSearch({
                ...expandSearch,
                [titleLowerCase]: value,
            })
        },
        [expandSearch, panelTitle]
    )

    const goSearch = React.useCallback(() => {
        setIsSearched({
            ...isSearched,
            [titleLowerCase]: true,
        })
        setBPSearchValue?.(searchValue[titleLowerCase], panelTitle)
    }, [searchValue, isSearched])

    const clearSearch = React.useCallback(() => {
        if (searchValue[titleLowerCase]) {
            if (isSearched[titleLowerCase as keyof typeof isSearched]) {
                setBPSearchValue?.('', panelTitle)
            }
            setSearchValue({ ...searchValue, [titleLowerCase]: '' })
            return
        }
        handleSearchExpand(false)
    }, [isSearched, searchValue, expandSearch])

    const handleSearch = React.useCallback(() => {
        if (isValueAllowedForSearch()) {
            return goSearch()
        }
        handleSearchExpand(true)
    }, [goSearch, searchValue, handleSearchExpand])

    return (
        <div
            className={classes.searchContainer}
            data-testid="elementPanelSearch"
        >
            <Input
                disableUnderline
                id={`search${panelTitle}`}
                inputProps={{
                    'aria-label': 'search',
                    id: `searchinput${panelTitle}`,
                    tabIndex: expandSearch[titleLowerCase] ? 0 : -1,
                }}
                classes={{
                    input: clsx(
                        classes.search,
                        expandSearch[titleLowerCase] && classes.expandRight,
                        classes.searchFieldText
                    ),
                    root: classes.searchFieldItem,
                }}
                placeholder={`Search ${panelTitle}`}
                value={searchValue[titleLowerCase]}
                onChange={handleSearchValue}
                onKeyDown={handleSearchKeyDown}
                endAdornment={
                    <InputAdornment position="end">
                        {expandSearch[titleLowerCase] && (
                            <IconButton
                                onClick={clearSearch}
                                aria-label="clear search field"
                                classes={{
                                    root: classes.adornmentButton,
                                }}
                                disableRipple
                                data-testid={`clearSearch-${titleLowerCase}`}
                            >
                                <ClearIcon className={classes.clearIcon} />
                            </IconButton>
                        )}
                        <IconButton
                            classes={{
                                root: clsx(classes.adornmentButton),
                            }}
                            onClick={handleSearch}
                            disableRipple
                            data-testid={`btnSearchField-${titleLowerCase}`}
                            aria-label="Search Field Button"
                            disabled={
                                !!expandSearch[titleLowerCase] &&
                                !!searchValue[titleLowerCase] &&
                                isValueAllowedForSearch('lessThan')
                            }
                        >
                            <SearchIcon
                                className={clsx(classes.searchIcon, {
                                    [classes.searchIconDisabled]:
                                        expandSearch[titleLowerCase] &&
                                        (!searchValue[titleLowerCase] ||
                                            isValueAllowedForSearch(
                                                'lessThan'
                                            )),
                                })}
                            />
                        </IconButton>
                    </InputAdornment>
                }
                data-testid={`searchField-${titleLowerCase}`}
            />
        </div>
    )
}

export default ElementPanelSearch
